<script lang="ts">
import {defineComponent, PropType} from 'vue'
import {isOfferApiResponseSuccess, OfferService} from "../../services/OfferService";
import {OfferApiResponse, OfferApiResponseSuccess} from "../../models/OfferApiResponse";
import type {OfferPageDto} from "../../models/OfferPageDto";
import SkeletonOfferDetailPage from "../../components/SkeletonOfferDetailPage.vue";
import Lien from "../../../common/components/ob1/Lien.vue";
import CommercialTag from "../../../common/components/ob1/CommercialTag.vue";
import Price from "../../../common/components/ob1/Price.vue";
import Breadcrumb from "../../../common/components/ob1/Breadcrumb.vue";
import LinkFoldUnFold from "../../../common/components/ob1/LinkFoldUnfold.vue";
import OptionEditoPopin from "../../../common/components/vue/OptionEditoPopin.vue";
import ThreeColumnsEditoPopin from "../../../common/components/vue/ThreeColumnsEditoPopin.vue";
import EditoPopin from "../../../common/components/vue/EditoPopin.vue";
import LegalMentions from "../../components/LegalMentions.vue";
import ModalDialog from "../../../common/components/ob1/ModalDialog.vue";
import ShowcaseFaq from "../../../orange/broadband/demands/showcase/internetandmobileoffers/components/ShowcaseFaq.vue";
import Bouton from "../../../common/components/ob1/Bouton.vue";
import {CartService} from "../../services/CartService";
import {OfferCartRequest, OfferCartResponseSuccess, OfferUniverse} from "../../models/CartApiResponse";
import ModalAfterAddToCart from "../../../common/components/custom/ModalAfterAddToCart.vue";
import DiscountInfoBanner from "../../../fmfmo/common/components/DiscountInfoBanner.vue";
import AiChatbotStarter from "../../components/AiChatbotStarter.vue";
import {AiChatbotStarterDto} from "../../models/AiChatbotStarterDto";
import {AiChatbotStarterResult, AiChatbotStarterService} from "../../services/AiChatbotStarterService";
import EditorialPush from "../../../common/components/ob1/EditorialPush.vue";
import {BreadcrumbItem} from "../../../common/components/ob1/models/BreadcrumbItem";

export default defineComponent({
    name: "OfferPage",
    components: {
        DiscountInfoBanner,
        ModalAfterAddToCart,
        EditorialPush,
        Bouton,
        ShowcaseFaq,
        ModalDialog,
        OptionEditoPopin, ThreeColumnsEditoPopin, EditoPopin,
        LinkFoldUnFold,
        Breadcrumb,
        LegalMentions, Price, CommercialTag, Lien, SkeletonOfferDetailPage, AiChatbotStarter
    },
    props: {
        dto: {
            type: Object as PropType<OfferPageDto>,
            required: true
        },
    },
    data() {
        return {
            offerApiResponse: null as OfferApiResponseSuccess | null,
            editoPopinRefs: [] as EditoPopin[],
            aiChatbotStarter: null as AiChatbotStarterDto | null,
        }
    },
    mounted() {
        AiChatbotStarterService.getAiChatbotStarter(this.dto.offerType).then((result: AiChatbotStarterResult) => {
            if (result.status === "FOUND") {
                this.aiChatbotStarter = result.starter;
            }
        });

        OfferService.getOffer(
            this.dto.seoId,
            this.dto.deviceSeoId ?? null,
            this.dto.showcaseContext,
            this.dto.offerType
        ).then((response: OfferApiResponse) => {
            if (isOfferApiResponseSuccess(response)) {
                this.offerApiResponse = response;

                this.addElementsForModalToggle();

                //update des données META de la page
                const descEl = document.querySelector('head meta[name="description"]');
                const titleEl = document.querySelector('head title');
                descEl?.setAttribute('content', this.offerApiResponse?.offer?.metaDescription);
                if (titleEl) {
                    titleEl.textContent = this.offerApiResponse?.offer?.metaTitle;
                }
            }
        });
    },
    updated() {
        this.addElementsForModalToggle();
    },
    computed: {
        ctaIsButton(): boolean {
            return !this.offerApiResponse?.buttonCta || Object.keys(this.offerApiResponse.formParams).length > 0;
        },
        breadcrumbItems(): Array<BreadcrumbItem> {
            return this.offerApiResponse?.breadcrumb?.rootLinks?.map(rootLink => ({
                href: rootLink.href ?? "#",
                label: rootLink.label,
            })) ?? [];
        }
    },
    methods: {
        addElementsForModalToggle() {
            const offerDetails = this.$refs.offerDetails
            if (offerDetails) {
                // Rééecriture des liens dans la div contenant les détails de l'offre
                offerDetails.querySelectorAll("a").forEach((link: HTMLAnchorElement) => {
                    this.rewriteAnchor(link);
                });
            }

            // Masquer les boutons des différentes modals
            const threeColumnsEditoPopin = this.$refs.threeColumnsEditoPopin;
            if (threeColumnsEditoPopin && threeColumnsEditoPopin.$el) {
                threeColumnsEditoPopin.$el.querySelectorAll("[data-bs-target]")
                    .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
            }
            const optionEditoPopin = this.$refs.optionEditoPopin;
            if (optionEditoPopin && optionEditoPopin.$el) {
                optionEditoPopin.$el.querySelectorAll("[data-bs-target]")
                    .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
            }
            const editoPopin = this.$refs.editoPopin;
            if (editoPopin && editoPopin.$el) {
                editoPopin.$el.querySelectorAll("[data-bs-target]")
                    .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
            }
            this.editoPopinRefs.forEach((editoPopin: EditoPopin) => {
                if (editoPopin && editoPopin.$el) {
                    editoPopin.$el.querySelectorAll("[data-bs-target]")
                        .forEach((element: HTMLElement) => { element.classList.add("d-none"); });
                }
            });
        },
        rewriteAnchor(link: HTMLAnchorElement) {
            const hrefAttr = link.getAttribute("href");
            if (!hrefAttr) return;

            if (hrefAttr.startsWith("#")) {
                link.target = "_self";
                link.dataset.bsToggle = "modal";
                link.dataset.oeventCategory = "detail_offre_modal";
                link.dataset.oeventLabel = toBroadbandTmsFormat(link.text);
                link.dataset.oeventAction = hrefAttr.slice(1);
                link.className = "ob1-link-arrow ob1-link-large";
            } else {
                link.target = "_blank";
                link.dataset.oeventCategory = "detail_offre_lien_externe";
                link.dataset.oeventLabel = toBroadbandTmsFormat(link.text);
                link.dataset.oeventAction = hrefAttr.slice(1);
            }
        },
        addEditoPopinRef(editoPopin: any, index: number, id: string): string {
            this.editoPopinRefs[index] = editoPopin as EditoPopin;
            return `editoPopin${id}`;
        },
        handleCtaClick(): void {
            if (this.ctaIsButton) {
                const buttons = ['topCtaButton', 'bottomCtaButton'];
                buttons.forEach(refName => this.$refs[refName].startAnimation());

                const cartRequest: OfferCartRequest = {
                    id: this.offerApiResponse.formParams['id'] || '',
                    device: this.offerApiResponse.formParams['device'] || null,
                    mobileId: this.offerApiResponse.formParams['mobileId'] || null,
                    universe: this.offerApiResponse.formParams['universe'] as OfferUniverse
                };

                CartService.setCart(cartRequest).then((response: OfferCartResponseSuccess) => {
                    buttons.forEach(refName => this.$refs[refName].stopAnimation());
                    if (this.offerApiResponse?.modalAfterAddToCart) {
                        this.$refs['modalAfterAddToCart'].show();
                    } else {
                        window.location.href = response.redirectUrl;
                    }
                }).catch(() => {
                    buttons.forEach(refName => this.$refs[refName].stopAnimation());
                });
            }
        }
    }
})
</script>

<template>
    <div class="bg-gray-2 text-dark offer-attributes">
        <div class="container-fluid pb-1 px-0">
            <template v-if="offerApiResponse">
                <discount-info-banner
                    v-if="offerApiResponse.discountInfoBanner"
                    :text="offerApiResponse.discountInfoBanner.text"
                    :overlay="offerApiResponse.discountInfoBanner.overlay"
                    class="discount-info-banner"
                />
                <div class="position-relative mb-1">
                    <!-- Header avec bouton retour -->
                    <div class="py-0">
                        <Lien
                            :is-link-arrow-back="true"
                            :href="offerApiResponse.backLink.url"
                            :label="'Retour'"
                            :tms-category="offerApiResponse.backLink.tms.category + '_haut'"
                            :tms-action="offerApiResponse.backLink.tms.action"
                            :tms-label="offerApiResponse.backLink.tms.label"
                        />
                    </div>

                    <!-- Bandeau bg-primary3 -->
                    <div class="bg-primary3 banner"></div>

                    <div class="bg-white text-black mx-1 mx-md-0 px-2 pb-1 mt-5 content-overlay">
                        <div class="row mb-1 header-title">
                            <!-- Tag + Image -->
                            <div class="col-12 d-flex justify-content-between position-relative">
                                <!-- Tag -->
                                <div class="col-6 col-lg-12 align-self-start commercial-tag">
                                    <CommercialTag
                                        v-if="offerApiResponse.offer.tag"
                                        :label="offerApiResponse.offer.tag"
                                        :version="'information'"
                                    />
                                </div>
                                <!-- Image produit -->
                                <img
                                    v-if="offerApiResponse.offer.visual.url"
                                    class="product-image"
                                    :src="offerApiResponse.offer.visual.url"
                                    :alt="offerApiResponse.offer.visual.alt"
                                />
                            </div>

                            <!-- Titre -->
                            <div class="col-12 col-md-9 mt-1">
                                <h1 class="mb-0">{{ offerApiResponse.offer.name }}</h1>
                                <span v-if="offerApiResponse.offer.attributes &&
                                    offerApiResponse.offer.attributes.title">
                                    {{ offerApiResponse.offer.attributes.title }}
                                </span>
                            </div>

                            <!-- Prix -->
                            <div class="col-12 col-md-3 mt-1">
                                <div class="d-flex flex-column align-items-md-end">
                                    <Price
                                        :price="offerApiResponse.offer.price.priceInCents"
                                        :initial-price="offerApiResponse.offer.price.initialPriceInCents"
                                        :show-by-month="offerApiResponse.offer.price.showByMonth"
                                        :show-from="offerApiResponse.offer.price.showFrom"
                                        :duration="offerApiResponse.offer.price.duration"
                                        :price-details="offerApiResponse.offer.price.priceDetails"
                                        :price-label="offerApiResponse.offer.price.priceLabel"
                                    />
                                </div>
                                <div
                                    v-if="offerApiResponse.buttonCta"
                                    class="d-flex flex-column align-items-center align-items-md-end mt-1 mt-md-0"
                                >
                                    <Bouton
                                        ref="topCtaButton"
                                        class="mw-100 w-100 w-md-auto"
                                        :is-button="ctaIsButton"
                                        :label="offerApiResponse.buttonCta.label"
                                        :href="offerApiResponse.buttonCta.href"
                                        :tms-category="offerApiResponse.buttonCta.tms.category + '_haut'"
                                        :tms-action="offerApiResponse.buttonCta.tms.action"
                                        :tms-label="offerApiResponse.buttonCta.tms.label"
                                        @click="handleCtaClick"
                                    />
                                </div>
                            </div>
                        </div>

                        <hr
                            class="separator separator--gray4 w-100 "
                            :class="offerApiResponse.buttonCta ? 'd-none d-md-block' : ''"
                        >

                        <!-- Attributs -->
                        <div v-if="offerApiResponse.offer.attributes" ref="offerDetails">
                            <div
                                v-for="(attribute, index) in offerApiResponse.offer.attributes.details"
                                :key="'offer-attribute-' + index"
                            >
                                <div class="py-1">
                                    <div class="d-flex my-1 align-items-start mb-1">
                                        <div class="me-1">
                                            <span class="h4 icon icon-livebox-s" :class="attribute.icon"></span>
                                        </div>
                                        <div class="d-flex flex-column w-100">
                                            <div v-safe-html="attribute.descriptionBbcode"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <hr
                            class="separator separator--gray4 w-100 "
                            :class="offerApiResponse.buttonCta ? 'd-none' : ''"
                        >

                        <div class="ob1-skeleton my-1">
                            <div class="back-button">
                                <Lien
                                    v-if="!offerApiResponse.buttonCta"
                                    :is-link-arrow-back="true"
                                    :href="offerApiResponse.backLink.url"
                                    :label="offerApiResponse.backLink.label"
                                    :tms-category="offerApiResponse.backLink.tms.category + '_bas'"
                                    :tms-action="offerApiResponse.backLink.tms.action"
                                    :tms-label="offerApiResponse.backLink.tms.label"
                                />
                            </div>
                        </div>
                    </div>

                    <!--/* ZONE STARTER CHATBOT IA */-->
                    <AiChatbotStarter
                        v-if="aiChatbotStarter"
                        :dto="aiChatbotStarter"
                        custom-class="pt-4 px-1 px-md-0"
                    />

                    <!--/* ZONE ZIGZAG */-->
                    <div v-if="offerApiResponse.offer.zigZag" class="pt-4 px-1 px-md-0">
                        <div class="bg-white px-2 py-2">
                            <EditorialPush :zigzag="offerApiResponse.offer.zigZag" />
                        </div>
                    </div>

                    <!--/* ZONE CTA BAS */-->
                    <div v-if="offerApiResponse.buttonCta" class="pt-4 px-1 px-md-0">
                        <div class="bg-white px-2 py-2">
                            <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-start">
                                <h3 class="mb-0 align-self-md-center">
                                    {{ offerApiResponse.offer.name }}
                                </h3>
                                <div class="d-flex flex-column align-items-start align-items-md-end">
                                    <Price
                                        :price="offerApiResponse.offer.price.priceInCents"
                                        :initial-price="offerApiResponse.offer.price.initialPriceInCents"
                                        :show-by-month="offerApiResponse.offer.price.showByMonth"
                                        :show-from="offerApiResponse.offer.price.showFrom"
                                        :duration="offerApiResponse.offer.price.duration"
                                        :price-details="offerApiResponse.offer.price.priceDetails"
                                        :price-label="offerApiResponse.offer.price.priceLabel"
                                    />
                                    <Bouton
                                        ref="bottomCtaButton"
                                        class="mw-100 w-100 w-md-auto align-self-center align-self-md-start"
                                        :is-button="ctaIsButton"
                                        :label="offerApiResponse.buttonCta.label"
                                        :href="offerApiResponse.buttonCta.href"
                                        :tms-category="offerApiResponse.buttonCta.tms.category + '_bas'"
                                        :tms-action="offerApiResponse.buttonCta.tms.action"
                                        :tms-label="offerApiResponse.buttonCta.tms.label"
                                        @click="handleCtaClick"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="offerApiResponse.offer.faq" class="pt-4 px-1 px-md-0">
                        <div class="bg-white px-2 py-2">
                            <ShowcaseFaq
                                :faq-title="offerApiResponse.offer.faq.faqTitle"
                                :faq-list-of-attributes="offerApiResponse.offer.faq.faqListOfAttributes"
                            />
                        </div>
                    </div>

                    <hr class="separator separator--gray4 w-100 mt-2">

                    <!--/* MENTIONS LEGALES */-->
                    <LegalMentions :legal-mentions="offerApiResponse.legalMentions" class="pt-2" />

                    <hr class="separator separator--gray4 w-100 mt-2">

                    <!--/* FIL ARIANE */-->
                    <div class="row px-1 px-md-0">
                        <div class="col-12 mt-1">
                            <Breadcrumb
                                :custom-aria-label="'Fil d\'ariane'"
                                :label="offerApiResponse.breadcrumb.lastElementLabel"
                                :breadcrumb-items="breadcrumbItems"
                            />
                        </div>
                    </div>

                    <div v-if="offerApiResponse.offer.attributes != null">
                        <!--/* POPINS INTERNET */-->
                        <template v-if="offerApiResponse.offer.attributes.genericPopins">
                            <EditoPopin
                                v-for="(editoPopin, index) in offerApiResponse.offer.attributes.genericPopins"
                                :key="editoPopin.id"
                                :ref="el => addEditoPopinRef(el, index, editoPopin.id)"
                                :popin-id="editoPopin.popinModal.id"
                                :title="editoPopin.title"
                                :visual="editoPopin.visual"
                                :sections="editoPopin.sections"
                                :mentions="editoPopin.mentions"
                            />
                        </template>
                        <OptionEditoPopin
                            v-if="offerApiResponse.offer.attributes.mobileOptionPopin"
                            ref="optionEditoPopin"
                            :popin-id="offerApiResponse.offer.attributes.mobileOptionPopin.popinModal.id"
                            :title="offerApiResponse.offer.attributes.mobileOptionPopin.title"
                            :visual="offerApiResponse.offer.attributes.mobileOptionPopin.visual"
                            :sections="offerApiResponse.offer.attributes.mobileOptionPopin.sections"
                            :mentions="offerApiResponse.offer.attributes.mobileOptionPopin.mentions"
                            :price="offerApiResponse.offer.attributes.mobileOptionPopin.priceInCents"
                            :alert-message="offerApiResponse.offer.attributes.mobileOptionPopin.alertMessage"
                        />
                        <ThreeColumnsEditoPopin
                            v-if="offerApiResponse.offer.attributes.destinationsPopin"
                            ref="threeColumnsEditoPopin"
                            :popin-id="offerApiResponse.offer.attributes.destinationsPopin.popinModal.id"
                            :title="offerApiResponse.offer.attributes.destinationsPopin.title"
                            :visual="offerApiResponse.offer.attributes.destinationsPopin.visual"
                            :sections="offerApiResponse.offer.attributes.destinationsPopin.sections"
                            :mentions="offerApiResponse.offer.attributes.destinationsPopin.mentions"
                            :first-column-sections="offerApiResponse.offer.attributes.destinationsPopin.firstColumnSections"
                            :second-column-sections="offerApiResponse.offer.attributes.destinationsPopin.secondColumnSections"
                            :third-column-sections="offerApiResponse.offer.attributes.destinationsPopin.thirdColumnSections"
                        />

                        <!--/* POPINS MOBILE */-->
                        <EditoPopin
                            v-if="offerApiResponse.offer.mobilePopinIncludedCountries"
                            ref="editoPopin"
                            :popin-id="offerApiResponse.offer.mobilePopinIncludedCountries.id"
                            :title="offerApiResponse.offer.mobilePopinIncludedCountries.title"
                            :visual="offerApiResponse.offer.mobilePopinIncludedCountries.visual"
                            :sections="offerApiResponse.offer.mobilePopinIncludedCountries.sections"
                            :mentions="offerApiResponse.offer.mobilePopinIncludedCountries.mentions"
                        />
                    </div>
                    <ModalAfterAddToCart
                        v-if="offerApiResponse.modalAfterAddToCart"
                        ref="modalAfterAddToCart"
                        :content="offerApiResponse.modalAfterAddToCart"
                        :offer-name="offerApiResponse.offer.name"
                        :modal-id="'intention-'+offerApiResponse.offer.seoId"
                        :first-intention-href="'/mobile/choisir-un-smartphone'"
                        :second-intention-href="'/panier'"
                        :tms-category="'fiche_offre'"
                    />
                </div>
            </template>
            <template v-else>
                <SkeletonOfferDetailPage />
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.bg-gray-2 {
    --bs-bg-opacity: 1;
    background-color: rgba(var(--bs-gray2-rgb), var(--bs-bg-opacity)) !important;
}

.commercial-tag {
    min-height: 0.875rem;
}

@media (min-width: 736px) {
    .w-md-auto {
        width: auto !important;
    }

    .commercial-tag {
        min-height: 1.625rem;
    }
};

.banner {
    height: 180px;
    width: 100%;
    position: absolute;
    top: 50px;
    z-index: 1;

    @media (min-width: 736px) {
        width: calc(100% + 3.125%);
        left: -1.5625%;
    }
    @media (min-width: 960px) {
        width: calc(100% + 6.25%);
        left: -3.125%;
    }
    @media (min-width: 1440px) {
        width: calc(100% + 90px);
        left: -45px;
    }
}

.header-title {
    min-height: 140px;
}

.content-overlay {
    position:relative;
    z-index: 2;
}

.product-image {
    width: 100px;
    height: 100px;
    position: absolute;
    right: 0;
    top: -60px;
    @media (min-width: 960px) {
        width: 160px;
        height: 160px;
        top: -90px;
    }
}

.description-skeleton {
    margin: 15px 20px;
    height: 16px;
}

.discount-info-banner {
    position:relative;
    @media (min-width: 736px) {
        width: calc(100% + 3.125%);
        left: -1.5625%;
    }
    @media (min-width: 960px) {
        width: calc(100% + 6.25%);
        left: -3.125%;
    }
    @media (min-width: 1440px) {
        width: calc(100% + 90px);
        left: -45px;
    }
}
</style>
